import React, {useEffect, useRef, useState} from 'react'
import {useOrganizations} from 'organization/providers/OrganizationsProvider'
import {organizationApiClient} from 'organization/organizationApiClient'
import {
  organizationEventRoutes,
  organizationRoutes,
} from 'organization/OrganizationRoutes'
import {useLocalizedNavigate} from 'i18n/useLocalizedNavigate'
import {useI18n} from 'i18n/I18nContext'
import {toast} from 'react-toastify'
import {FullPageLoader} from 'organization/theme/FullPageLoader'
import {
  peekPendingEventDraft,
  clearPendingEventDraft,
} from 'organization/CreateEventOnboarding/pendingEventDraftStorage'
import {CreateEventData} from 'organization/Event/CreateEventData'

/**
 * When the user returns from the org login/register flow with a stored draft, create the
 * event via the API and open its settings.
 */
export function CompletePendingEventDraft() {
  const {organizations} = useOrganizations()
  const navigate = useLocalizedNavigate()
  const {t} = useI18n()
  const [busy, setBusy] = useState(false)
  const ran = useRef(false)

  useEffect(() => {
    if (!organizations || ran.current) {
      return
    }
    const draft = peekPendingEventDraft()
    if (!draft) {
      return
    }
    if (organizations.length === 0) {
      return
    }

    ran.current = true
    const org = organizations[0]!
    const payload: CreateEventData = {
      name: draft.name,
      slug: draft.slug,
      locale: draft.locale,
    }

    setBusy(true)
    const api = organizationApiClient()
    ;(async () => {
      try {
        const event = await api.post(
          `api/organization/${org.id}/event/create`,
          payload,
        )
        if (event && typeof event === 'object' && 'id' in event) {
          clearPendingEventDraft()
          toast.success(t('createEventWizard.createdSuccess'))
          navigate(
            organizationEventRoutes(
              event as {id: number},
              organizationRoutes(org),
            ).settings,
          )
        } else {
          ran.current = false
        }
      } catch (error: any) {
        ran.current = false
        const message =
          error?.response?.data?.message ||
          error?.message ||
          t('createEventWizard.createFailed')
        toast.warning(message)
      } finally {
        setBusy(false)
      }
    })()
  }, [organizations, navigate, t])

  return busy ? <FullPageLoader /> : null
}
